import i18n from '@/plugins/i18n'

/**
 * 创建枚举的工厂函数
 * @param {Object} obj 枚举对象
 * @returns {Object} 带有辅助方法的枚举对象
 */
export function createEnum(obj) {
  const enumObj = { ...obj }

  /**
   * 根据值查找枚举项
   * @param {any} value 要查找的值
   * @returns {any} 找到的枚举项或undefined
   */
  enumObj.findByValue = (value) => {
    if (value === null || value === undefined) {
      return undefined
    }
    return Object.values(obj).find(item => item.value === value)
  }

  /**
   * 根据标签查找枚举项
   * @param {any} label 要查找的标签
   * @returns {any} 找到的枚举项或undefined
   */
  enumObj.findByLabel = (label) => {
    if (label === null || label === undefined) {
      return undefined
    }
    return Object.values(obj).find(item => item.label === label)
  }

  /**
   * 获取所有键
   * @returns {string[]} 所有枚举键的数组
   */
  enumObj.keys = () => {
    return Object.keys(obj)
  }

  /**
   * 获取所有选项
   * @returns {any[]} 所有枚举项的数组
   */
  enumObj.options = () => {
    return Object.values(obj)
  }

  /**
   * 获取所有标签
   * @returns {string[]} 所有标签的数组
   */
  enumObj.labels = () => {
    return Object.values(obj).map(({ label }) => label)
  }

  /**
   * 获取所有值
   * @returns {any[]} 所有枚举项的值的数组
   */
  enumObj.values = () => {
    return Object.values(obj).map(({ value }) => value)
  }

  /**
   * 根据值获取标签
   * @param {any} value 要查找的值
   * @returns {string} 对应的标签或undefined
   */
  enumObj.getLabelByValue = (value) => {
    return enumObj.findByValue(value)?.label
  }

  /**
   * 根据标签获取值
   * @param {any} label 要查找的标签
   * @returns {any} 对应的值或undefined
   */
  enumObj.getValueByLabel = (label) => {
    return enumObj.findByLabel(label)?.value
  }

  /**
   * 根据值获取指定属性
   * @param {any} value 要查找的值
   * @param {string} key 要获取的属性名
   * @returns {any} 对应的属性值或undefined
   */
  enumObj.getKeyByValue = (value, key) => {
    return enumObj.findByValue(value)?.[key]
  }

  /**
   * 判断值是否在枚举中
   * @param {any} value 要验证的值
   * @returns {boolean} 是否存在
   */
  enumObj.validate = (value) => {
    return Object.values(obj).some(item => item.value === value)
  }

  return enumObj
}

/**
 * 设备类型
 */
export const DeviceTypeEnums = createEnum({
  DESKTOP: { label: '桌面', value: 'desktop' },
  MOBILE: { label: '移动', value: 'mobile' }
})

/**
 * 权限判断模式
 */
export const PermissionModeEnums = createEnum({
  AND: { label: '且', value: 'and' },
  OR: { label: '或', value: 'or' }
})

/**
 * 操作日志 - 业务类型
 */
export const BusinessTypeEnums = createEnum({
  OTHER: { label: i18n.t('const.BusinessTypeEnums.OTHER'), value: 0 },
  QUERY: { label: i18n.t('const.BusinessTypeEnums.QUERY'), value: 1 },
  INSERT: { label: i18n.t('const.BusinessTypeEnums.INSERT'), value: 2 },
  UPDATE: { label: i18n.t('const.BusinessTypeEnums.UPDATE'), value: 3 },
  DELETE: { label: i18n.t('const.BusinessTypeEnums.DELETE'), value: 4 },
  EXPORT: { label: i18n.t('const.BusinessTypeEnums.EXPORT'), value: 5 },
  EXPORT_TEMPLATE: { label: i18n.t('const.BusinessTypeEnums.EXPORT_TEMPLATE'), value: 6 },
  IMPORT: { label: i18n.t('const.BusinessTypeEnums.IMPORT'), value: 7 },
  LOGIN: { label: i18n.t('const.BusinessTypeEnums.LOGIN'), value: 8 },
  LOGOUT: { label: i18n.t('const.BusinessTypeEnums.LOGOUT'), value: 9 },
  KICKOUT: { label: i18n.t('const.BusinessTypeEnums.KICKOUT'), value: 10 },
  FORCE_LOGOUT: { label: i18n.t('const.BusinessTypeEnums.FORCE_LOGOUT'), value: 11 },
  PAUSE_RESUME_JOB: { label: i18n.t('const.BusinessTypeEnums.PAUSE_RESUME_JOB'), value: 12 },
  EXEC_JOB: { label: i18n.t('const.BusinessTypeEnums.EXEC_JOB'), value: 13 }
})

/**
 * 菜单类型
 */
export const MenuTypeEnums = createEnum({
  DIR: { label: i18n.t('const.MenuTypeEnums.DIR'), value: 1 },
  MENU: { label: i18n.t('const.MenuTypeEnums.MENU'), value: 2 },
  BUTTON: { label: i18n.t('const.MenuTypeEnums.BUTTON'), value: 3 }
})

export const DataScopeEnums = createEnum({
  ALL: { label: i18n.t('const.DataScopeEnums.ALL'), value: 1 },
  CUSTOM: { label: i18n.t('const.DataScopeEnums.CUSTOM'), value: 2 },
  DEPT: { label: i18n.t('const.DataScopeEnums.DEPT'), value: 3 },
  DEPT_AND_CHILD: { label: i18n.t('const.DataScopeEnums.DEPT_AND_CHILD'), value: 4 },
  SELF: { label: i18n.t('const.DataScopeEnums.SELF'), value: 5 }
})

export const NoticeTypeEnums = createEnum({
  INFO: { label: i18n.t('const.NoticeTypeEnums.INFO'), value: 0 },
  ANNOUNCEMENT: { label: i18n.t('const.NoticeTypeEnums.ANNOUNCEMENT'), value: 1 }
})

/**
 * 页面模式
 */
export const PageMode = createEnum({
  Add: {
    label: '新增',
    value: 'add'
  },
  Edit: {
    label: '编辑',
    value: 'edit'
  },
  Detail: {
    label: '详情',
    value: 'detail'
  }
})
